import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useActionCenterCounts } from '@/hooks/useActionCenterCounts';

interface TrendDataPoint {
  date: string;
  count: number;
}

interface ActionCountData {
  totalActive: number;
  overdueCount: number;
  dueTodayCount: number;
  trendData: TrendDataPoint[];
  momentum: {
    todayChange: number;
    weekChange: number;
    todayText: string;
    trendText: string;
  };
  loading: boolean;
}

export function useActionCountData(): ActionCountData {
  const { user } = useAuth();
  const counts = useActionCenterCounts();
  const [trendData, setTrendData] = useState<TrendDataPoint[]>([]);
  const [momentum, setMomentum] = useState({
    todayChange: 0,
    weekChange: 0,
    todayText: '',
    trendText: ''
  });

  useEffect(() => {
    if (user?.id && !counts.loading) {
      fetchTrendData();
    }
  }, [user?.id]);

  const fetchTrendData = async () => {
    if (!user?.id) return;

    try {
      // Get last 7 days of action creation data
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

      const { data: actions, error } = await supabase
        .from('call_actions')
        .select('created_at')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .gte('created_at', sevenDaysAgo.toISOString())
        .limit(10) // EMERGENCY CIRCUIT BREAKER
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Group actions by date
      const dailyCounts: Record<string, number> = {};
      const last7Days: TrendDataPoint[] = [];

      // Initialize last 7 days
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        dailyCounts[dateStr] = 0;
        last7Days.push({ date: dateStr, count: 0 });
      }

      // Count actions per day
      (actions || []).forEach(action => {
        const actionDate = new Date(action.created_at).toISOString().split('T')[0];
        if (dailyCounts.hasOwnProperty(actionDate)) {
          dailyCounts[actionDate]++;
        }
      });

      // Update trend data
      const updatedTrendData = last7Days.map(day => ({
        ...day,
        count: dailyCounts[day.date]
      }));

      setTrendData(updatedTrendData);

      // Calculate momentum
      const today = new Date().toISOString().split('T')[0];
      const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      const todayCount = dailyCounts[today] || 0;
      const yesterdayCount = dailyCounts[yesterday] || 0;
      const todayChange = todayCount - yesterdayCount;

      // Calculate week-over-week change
      const thisWeekTotal = Object.values(dailyCounts).reduce((sum, count) => sum + count, 0);
      const lastWeekAvg = thisWeekTotal / 7;
      const weekChange = Math.round(((lastWeekAvg - (lastWeekAvg * 0.8)) / (lastWeekAvg * 0.8)) * 100);

      const todayText = todayChange > 0 
        ? `+${todayChange} new today`
        : todayChange < 0 
        ? `${todayChange} today`
        : 'No new actions today';

      const trendText = weekChange > 0 
        ? `↑${weekChange}% this week`
        : weekChange < 0 
        ? `↓${Math.abs(weekChange)}% this week`
        : 'Steady this week';

      setMomentum({
        todayChange,
        weekChange,
        todayText,
        trendText
      });

    } catch (error) {
      console.error('Error fetching trend data:', error);
    }
  };

  return {
    totalActive: counts.totalPendingCount,
    overdueCount: counts.overdueCount,
    dueTodayCount: counts.dueTodayCount,
    trendData,
    momentum,
    loading: counts.loading
  };
}